const emittedEvents = new Map();
const eventListeners = new Map();
const defaultOptions = {
    retro: false,
    retroStrategy: 'last-one',
    once: false,
    unique: false
};
/**
 * Add a listener to a specific event.
 * @param name name of the event
 * @param callback the method who will be called when the event is emitted.
 * @param options option parameters to change callback behavior
 */
export const addEventBusListener = (name, callback, options = defaultOptions) => {
    const unsubscribe = () => removeEventBusListener(name, callback);
    const listeners = eventListeners.get(name);
    if (options.retro && emittedEvents.has(name)) {
        const emittedEventArgs = emittedEvents.get(name);
        switch (options.retroStrategy) {
            case 'all': {
                for (const args of emittedEventArgs) {
                    callback(...args);
                }
                break;
            }
            case 'last-one':
            default: {
                const args = emittedEventArgs[emittedEventArgs.length - 1];
                callback(...args);
                break;
            }
        }
        if (options.once) {
            return unsubscribe;
        }
    }
    const listener = Object.assign({ callback }, options);
    if (!listeners) {
        eventListeners.set(name, [listener]);
        return unsubscribe;
    }
    if (options.unique && listeners.find((c) => c.callback === callback)) {
        return unsubscribe;
    }
    eventListeners.set(name, [...listeners, listener]);
    return unsubscribe;
};
/**
 * Remove a callback to be called when event is emitted.
 * @param name name of the event.
 * @param callback callback you don't want anymore to trigger when event is emitted.
 */
export const removeEventBusListener = (name, callback) => {
    const calls = eventListeners.get(name);
    if (!calls) {
        return;
    }
    eventListeners.set(name, calls.filter((call) => call.callback !== callback));
};
/**
 * Clear all listeners from an event.
 * @param name event name to clear all its listeners.
 */
export const clearEventBusListeners = (name) => {
    if (name === undefined) {
        eventListeners.clear();
        return;
    }
    eventListeners.delete(name);
};
/**
 * Emit an event.
 * @param name name of the event to emit.
 * @param]} args arguments to be passed to all listeners.
 */
export const emit = (name, ...args) => {
    const listeners = eventListeners.get(name);
    if (emittedEvents.has(name)) {
        const emittedEventArgs = emittedEvents.get(name);
        emittedEvents.set(name, [...emittedEventArgs, args]);
    }
    else {
        emittedEvents.set(name, [args]);
    }
    if (!listeners) {
        return;
    }
    listeners.map((call) => call.callback(...args));
    eventListeners.set(name, listeners.filter((call) => !call.once));
};
